import { useSelector } from 'react-redux';
import { RootState } from 'redux/store';
import React, { useEffect, useState } from 'react';
import Button from 'components/Button/Index';
import Image from 'components/image/Image';
import useLocales from 'hooks/useLocales';
import { CMSService } from 'services/cms/cms';
import { IMe } from 'core/interface/me';
import { useParams } from 'react-router';
import { CandidateService } from 'services/candidate/candidate';
import RadarChartComp from 'components/shared/RadarChart/RadarChartComp';
import { calculateMatchPercentage } from 'helpers/teaserView';
import colors from 'assets/variables/colors';
import CultureProfiles from 'components/cultureProfiles';
import PopUpWrapper from 'components/PopUpWrapper';

function Culture({ canEdit }: { canEdit: boolean }) {
  const { t } = useLocales();
  const [openModal, setOpenModal] = useState(false);
  const { cms } = useSelector((state: RootState) => state.cms);
  const me = useSelector((state: IMe) => state.user).user;
  const { id, candidate_Id } = useParams();
  const { candidatesOverview } = useSelector(
    (state: RootState) => state.candidates
  );
  const [showMatch, setShowMatch] = useState(false);
  const [showChart, setShowChart] = useState(false);

  const handleModal = () => {
    setOpenModal(!openModal);
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      setShowChart(true);
    }, 700);
    return () => clearTimeout(timer);
  }, [openModal]);

  // Prepare radar chart data for both company and candidate
  const companyCompetencies = cms?.primary_culture_profile?.competencies || [];
  const labels = companyCompetencies.map((el: any) => el?.competency?.name);
  const minScores = companyCompetencies.map(
    (competency: any) => competency.min_score
  );
  const descriptions = companyCompetencies.map(
    (competency: any) => competency?.competency?.description
  );

  // Prepare candidate scores for these competencies
  let candidateCultureScores: number[] = [];
  if (
    candidatesOverview?.competencies &&
    candidatesOverview?.competencies.length > 0
  ) {
    // Map candidate's scores by competency_id
    const candidateScoresById: Record<number, number> = {};
    candidatesOverview.competencies.forEach((c: any) => {
      candidateScoresById[c.competency_id] = Number(c.score);
    });
    // Map to the order of company competencies
    candidateCultureScores = companyCompetencies.map(
      (c: any) => candidateScoresById[c.competency.id] ?? null
    );
  }

  // Compose datasets for RadarChartComp
  const radarDatasets = [
    {
      label: t('Company Min Scores'),
      data: minScores,
      backgroundColor: 'rgba(0, 158, 226, 0.50)',
      borderColor: '#009EE2',
      borderWidth: 1
    }
  ];

  if (
    candidateCultureScores.length &&
    candidateCultureScores.some((v) => v !== null)
  ) {
    radarDatasets.push({
      label: t('Candidate Scores'),
      data: candidateCultureScores,
      backgroundColor: 'rgba(82, 37, 131, 0.20)',
      borderColor: colors.tenantColor,
      borderWidth: 1.5
    });
  }

  // Map candidateCultureScores to array of objects for comparePoints
  const comparePoints =
    showMatch && candidateCultureScores && candidateCultureScores.length
      ? candidateCultureScores.map((score) => ({ score }))
      : [];

  // Calculate culture match percentage
  let cultureMatchPercent: string | null = null;
  if (
    showMatch &&
    candidateCultureScores.length &&
    candidateCultureScores.some((v) => v !== null) &&
    minScores.length === candidateCultureScores.length
  ) {
    // Replace nulls with 0 for calculation
    const safeCandidateScores = candidateCultureScores.map((v) =>
      v === null ? 0 : v
    );
    cultureMatchPercent = calculateMatchPercentage(
      safeCandidateScores,
      minScores
    );
  }

  const handleChange = (companyData: any) => {
    const formData = new FormData();
    for (const key in companyData) {
      if (key !== 'id') {
        formData.append(key, (companyData as any)[key]);
      }
    }
    CMSService.updateCMS(me?.company, formData);
    handleModal();
    setShowChart(false);
  };

  const getCandidateOverview = () => {
    CandidateService.getCandidateOverview(candidate_Id ? +candidate_Id : -1);
  };

  useEffect(() => {
    candidate_Id && getCandidateOverview();
  }, [candidate_Id]);

  return (
    <div className="cultureProfileContainer">
      <div className="cultureProfileContainer">
        <div className="cultureProfile">
          <h1>{t('Culture profile')}</h1>
          {canEdit && (
            <div className="pencil_icon" onClick={handleModal}>
              <div className="pencil_circle" />
              <Image
                className="pencil"
                name="pencil.svg"
                width={13}
                height={13}
              />
            </div>
          )}
        </div>
        {me?.candidate_id && (
          <>
            <span>{t('Do you have a match with us?')}</span>
            <Button
              className="next"
              title={t('Match now')}
              t={t}
              onClick={() => setShowMatch(true)}
            />
          </>
        )}
      </div>
      <div className="cultureProfileChart">
        <div
          style={{
            textAlign: 'center',
            fontWeight: 600,
            fontSize: 18,
            marginBottom: 12,
            color: cultureMatchPercent !== null ? '#000' : 'transparent'
          }}
        >
          {t('Culture Match')}: {cultureMatchPercent}%
        </div>

        {showChart && (
          <RadarChartComp
            boxSx={{ width: '100%' }}
            labels={labels}
            points={minScores}
            descriptions={descriptions}
            comparePoints={comparePoints}
            assessment={true}
            chartHeight={500}
            chartWidth={500}
          />
        )}
      </div>
      {openModal && (
        <PopUpWrapper onClick={handleModal}>
          <CultureProfiles
            companyData={cms}
            setNewData={handleChange}
            isOneBtn={true}
            onClose={handleModal}
            showTopSide={false}
            isPopup={true}
            showTitle
          />
        </PopUpWrapper>
      )}
    </div>
  );
}

export default Culture;
